import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Search, Download, ChevronLeft, ChevronRight, Boxes } from "lucide-react";
import { Package, AlertTriangle } from "lucide-react";
import Barcode from "react-barcode";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/lib/supabase";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const generateEAN13 = () => {
  let code = '200';
  for (let i = 0; i < 9; i++) {
    code += Math.floor(Math.random() * 10);
  }
  
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(code[i]) * (i % 2 === 0 ? 1 : 3);
  }
  const checkDigit = (10 - (sum % 10)) % 10;
  return code + checkDigit;
};

interface Product {
  product_id: number;
  code: string;
  product_name: string;
  category_name: string;
  department_id: number;
  department_name: string;
  department_sigla: string;
  quantity: number;
  initial_quantity: number;
  percentage: string;
  status: string;
  unit: string;
  supplier_name: string;
}

interface Department {
  id: number;
  name: string;
}

interface Category {
  id: number;
  name: string;
}

const Products = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchByCode, setSearchByCode] = useState("");
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showLowStockAlert, setShowLowStockAlert] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalLowStock, setTotalLowStock] = useState(0);
  const [totalCategories, setTotalCategories] = useState(0);

  // Função para normalizar texto (remover acentos e caracteres especiais)
  const normalizeText = (text: string) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  useEffect(() => {
    fetchDepartments();
    fetchCategories();
    fetchTotalStats();
  }, []);

  useEffect(() => {
    if (selectedDepartment) {
      fetchProducts();
      fetchTotalStats();
    }
  }, [selectedDepartment, selectedCategory, searchTerm, searchByCode, currentPage]);

  useEffect(() => {
    const hasLowStock = products.some(product => 
      (product.quantity / product.initial_quantity) * 100 <= 80
    );
    setShowLowStockAlert(hasLowStock);
  }, [products]);

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setDepartments(data || []);
      
      // Se houver apenas um departamento, seleciona automaticamente
      if (data && data.length === 1) {
        setSelectedDepartment(data[0].id.toString());
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as secretarias",
        variant: "destructive",
      });
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as categorias",
        variant: "destructive",
      });
    }
  };

  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      // Primeiro, buscar todos os produtos únicos
      let query = supabase
        .from('products')
        .select(`
          id,
          code,
          name,
          unit,
          supplier_id,
          suppliers (
            id,
            name
          ),
          categories (
            id,
            name
          ),
          inventory:inventory (
            quantity,
            initial_quantity,
            department_id,
            departments:departments (
              id,
              name,
              sigla
            )
          )
        `, { count: 'exact' });

      const { data: productsData, error: productsError, count } = await query;

      if (productsError) throw productsError;

      if (productsData) {
        // Filtrar produtos que têm pelo menos um registro no inventário
        let filteredData = productsData.filter(product => 
          product.inventory && product.inventory.length > 0
        );

        // Filtro por departamento
        if (selectedDepartment && selectedDepartment !== 'all') {
          console.log(`Filtrando produtos por departamento: ${selectedDepartment}`);
          // Filtra apenas produtos que têm inventário no departamento selecionado
          filteredData = filteredData.filter(product => 
            product.inventory.some(inv => inv.department_id.toString() === selectedDepartment)
          );
          
          // Para cada produto, mantenha apenas o inventário do departamento selecionado
          filteredData = filteredData.map(product => {
            const filteredInventory = product.inventory.filter(inv => 
              inv.department_id.toString() === selectedDepartment
            );
            
            return {
              ...product,
              inventory: filteredInventory
            };
          });
        }

        // Filtro por categoria
        if (selectedCategory && selectedCategory !== 'all') {
          filteredData = filteredData.filter(product => 
            product.categories?.id.toString() === selectedCategory
          );
        }

        // Melhorando o filtro por nome
        if (searchTerm) {
          const normalizedSearchTerm = normalizeText(searchTerm);
          filteredData = filteredData.filter(product => {
            if (!product.name) return false;
            const normalizedProductName = normalizeText(product.name);
            
            // Primeiro tenta uma correspondência parcial mais flexível
            if (normalizedProductName.includes(normalizedSearchTerm)) {
              return true;
            }
            
            // Se não encontrar, procura por palavras individuais
            const searchWords = normalizedSearchTerm.split(' ').filter(word => word.length > 0);
            const productWords = normalizedProductName.split(' ');
            
            // Retorna true se pelo menos uma palavra da busca estiver presente
            return searchWords.some(searchWord =>
              productWords.some(productWord => productWord.includes(searchWord))
            );
          });
        }

        // Filtro por código
        if (searchByCode) {
          const normalizedSearchCode = normalizeText(searchByCode);
          filteredData = filteredData.filter(product => 
            product.code && normalizeText(product.code).includes(normalizedSearchCode)
          );
        }

        // Sort the filtered data
        const sortedData = filteredData.sort((a, b) => 
          (a.name || '').localeCompare(b.name || '')
        );

        // Apply pagination
        const from = (currentPage - 1) * itemsPerPage;
        const to = from + itemsPerPage;
        const paginatedData = sortedData.slice(from, to);

        console.log("Dados dos produtos antes da formatação:", paginatedData);
        
        const formattedProducts: Product[] = paginatedData.map((product: any) => {
          // Para cada produto, filtrar o inventário pelo departamento selecionado
          console.log(`Processando produto: ${product.name}, ID: ${product.id}`);
          console.log(`Inventário do produto:`, product.inventory);
          
          // Filtrar inventário pelo departamento selecionado
          const inventoryItems = selectedDepartment !== 'all' 
            ? product.inventory.filter(inv => inv.department_id.toString() === selectedDepartment)
            : product.inventory;
            
          console.log(`Itens de inventário após filtro (departamento ${selectedDepartment}):`, inventoryItems);

          // Calcular quantidade e quantidade inicial
          let quantity = 0;
          let initial_quantity = 0;
          let departmentInfo = { id: 0, name: 'Sem departamento', sigla: '-' };
          
          if (inventoryItems.length > 0) {
            if (selectedDepartment !== 'all') {
              // Se um departamento específico foi selecionado, use apenas esse
              // Garantir que estamos usando o departamento correto
              const deptItem = inventoryItems[0];
              
              // Verificar se temos informações do departamento
              if (!deptItem.departments) {
                console.error(`Departamento não encontrado para o item:`, deptItem);
              }
              
              quantity = deptItem.quantity || 0;
              initial_quantity = deptItem.initial_quantity || 0;
              
              // Usar informações do departamento selecionado
              departmentInfo = {
                id: deptItem.department_id,
                name: deptItem.departments?.name || 'Departamento desconhecido',
                sigla: deptItem.departments?.sigla || '-'
              };
              
              console.log(`Usando departamento específico: ${departmentInfo.name} (${departmentInfo.sigla})`);
              console.log(`Quantidade: ${quantity}, Quantidade inicial: ${initial_quantity}`);
            } else if (inventoryItems.length === 1) {
              // Se há apenas um departamento no inventário
              const singleItem = inventoryItems[0];
              quantity = singleItem.quantity || 0;
              initial_quantity = singleItem.initial_quantity || 0;
              
              departmentInfo = {
                id: singleItem.department_id,
                name: singleItem.departments?.name || 'Departamento desconhecido',
                sigla: singleItem.departments?.sigla || '-'
              };
              
              console.log(`Usando único departamento: ${departmentInfo.name} (${departmentInfo.sigla})`);
            } else {
              // Se há múltiplos departamentos e nenhum específico foi selecionado
              // Somar as quantidades de todos os departamentos
              quantity = inventoryItems.reduce((sum, inv) => sum + (inv.quantity || 0), 0);
              initial_quantity = inventoryItems.reduce((sum, inv) => sum + (inv.initial_quantity || 0), 0);
              
              departmentInfo = {
                id: 0,
                name: 'Múltiplos departamentos',
                sigla: 'MULT'
              };
              
              console.log(`Usando múltiplos departamentos: quantidade total = ${quantity}`);
            }
          }
          
          const percentage = initial_quantity > 0 ? ((quantity / initial_quantity) * 100).toFixed(0) : '0';

          return {
            product_id: product.id,
            code: product.code || '',
            product_name: product.name || 'Produto sem nome',
            category_name: product.categories?.name || 'Sem categoria',
            department_id: departmentInfo.id,
            department_name: departmentInfo.name,
            department_sigla: departmentInfo.sigla,
            quantity: quantity,
            initial_quantity: initial_quantity,
            percentage: percentage,
            status: Number(percentage) <= 80 ? 'baixo' : 'normal',
            unit: product.unit || 'UN',
            supplier_name: product.suppliers?.name || 'Não informado'
          };
        });

        setProducts(formattedProducts);
        setTotalPages(Math.ceil(filteredData.length / itemsPerPage));
      }
    } catch (error: any) {
      console.error('Error fetching products:', error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível carregar os produtos",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const downloadBarcode = (code: string) => {
    const canvas = document.querySelector(`#barcode-${code} canvas`) as HTMLCanvasElement;
    if (canvas) {
      const link = document.createElement('a');
      link.download = `barcode-${code}.png`;
      link.href = canvas.toDataURL();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "Código de barras baixado",
        description: `O código ${code} foi baixado com sucesso.`,
      });
    }
  };

  const handleViewDetails = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleEdit = (product: Product) => {
    navigate(`/products/edit/${product.product_id}`);
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Tem certeza que deseja excluir este produto?")) return;

    setIsLoading(true);
    try {
      // Primeiro excluir do inventário
      const { error: inventoryError } = await supabase
        .from('inventory')
        .delete()
        .eq('product_id', id);

      if (inventoryError) throw inventoryError;

      // Depois excluir o produto
      const { error: productError } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (productError) throw productError;

      toast({
        title: "Sucesso",
        description: "Produto excluído com sucesso",
      });
      
      fetchProducts();
    } catch (error: any) {
      console.error('Error deleting product:', error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível excluir o produto",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTotalStats = async () => {
    try {
      let query;

      // Busca do total geral de produtos únicos
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('id');

      if (productsError) throw productsError;

      // Busca produtos com estoque baixo
      const { data: inventoryData, error: inventoryError } = await supabase
        .from('inventory')
        .select(`
          product_id,
          quantity,
          initial_quantity,
          department_id
        `);

      if (inventoryError) throw inventoryError;

      let totalProducts = 0;
      let lowStockProducts = 0;

      if (selectedDepartment && selectedDepartment !== 'all') {
        // Filtra produtos por departamento
        const departmentInventory = inventoryData.filter(item => 
          item.department_id.toString() === selectedDepartment
        );
        
        totalProducts = departmentInventory.length;
        lowStockProducts = departmentInventory.filter(item => {
          const quantity = item.quantity || 0;
          const initialQuantity = item.initial_quantity || 0;
          return initialQuantity > 0 && (quantity / initialQuantity) * 100 <= 80;
        }).length;
      } else {
        // Total geral de produtos únicos
        totalProducts = productsData?.length || 0;
        lowStockProducts = inventoryData.filter(item => {
          const quantity = item.quantity || 0;
          const initialQuantity = item.initial_quantity || 0;
          return initialQuantity > 0 && (quantity / initialQuantity) * 100 <= 80;
        }).length;
      }

      // Get categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*');

      if (categoriesError) throw categoriesError;

      setTotalProducts(totalProducts);
      setTotalLowStock(lowStockProducts);
      setTotalCategories(categoriesData?.length || 0);
    } catch (error: any) {
      console.error('Error fetching total stats:', error);
      toast({
        title: "Erro",
        description: error.message || "Não foi possível carregar as estatísticas",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'normal':
        return (
          <div className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
            Normal
          </div>
        );
      case 'baixo':
        return (
          <div className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
            Baixo
          </div>
        );
      default:
        return (
          <div className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20">
            {status}
          </div>
        );
    }
  };

  const getProductStatus = (quantity: number, initialQuantity: number) => {
    if (quantity <= 0) return 'inactive';
    const percentage = (quantity / initialQuantity) * 100;
    if (percentage <= 80) return 'baixo';
    return 'normal';
  };

  return (
    <div className="space-y-6 p-6">
      {showLowStockAlert && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Atenção! Existem produtos com estoque baixo (quantidade menor ou igual a 80% do inicial).
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-800">Total de Produtos</CardTitle>
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
              <Package className="h-4 w-4 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-700">{totalProducts}</div>
            <p className="text-xs text-blue-600">
              Produtos cadastrados no sistema
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-red-800">Estoque Baixo</CardTitle>
            <div className="h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-700">{totalLowStock}</div>
            <p className="text-xs text-red-600">
              Produtos com menos de 80% do estoque inicial
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-emerald-800">Categorias</CardTitle>
            <div className="h-8 w-8 rounded-full bg-emerald-100 flex items-center justify-center">
              <Boxes className="h-4 w-4 text-emerald-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">{totalCategories}</div>
            <p className="text-xs text-emerald-600">
              Categorias cadastradas
            </p>
          </CardContent>
        </Card>
      </div>
      
      <div className="flex flex-col space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold">Produtos</h1>
          <Button asChild>
            <Link to="/products/new">
              <Plus className="mr-2 h-4 w-4" /> Novo Produto
            </Link>
          </Button>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-1/3">
            <Select
              value={selectedDepartment}
              onValueChange={setSelectedDepartment}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma secretaria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as secretarias</SelectItem>
                {departments.map((department) => (
                  <SelectItem key={department.id} value={department.id.toString()}>
                    {department.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full md:w-1/3">
            <Select
              value={selectedCategory}
              onValueChange={setSelectedCategory}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias</SelectItem>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id.toString()}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1">
            <Input
              placeholder="Buscar por nome "
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex-1">
            <Input
              placeholder="Buscar por código"
              value={searchByCode}
              onChange={(e) => setSearchByCode(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Fornecedor</TableHead>
              <TableHead>Secretaria</TableHead>
              <TableHead>Quantidade</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.product_id}>
                <TableCell className="font-mono">
                  <div className="flex items-center gap-2">
                    {product.code}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => downloadBarcode(product.code)}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
                <TableCell>{product.product_name}</TableCell>
                <TableCell>{product.category_name}</TableCell>
                <TableCell>{product.supplier_name}</TableCell>
                <TableCell>
                  <span title={product.department_name}>{product.department_sigla}</span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {product.quantity} {product.unit}
                    <span className="text-xs text-muted-foreground">
                      ({product.percentage}%)
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  {getStatusBadge(product.status)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleViewDetails(product)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(product)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(product.product_id)}
                      disabled={isLoading}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {products.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-4">
                  Nenhum produto encontrado
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination Controls */}
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <div className="flex-1 flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Mostrando <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> a{" "}
              <span className="font-medium">
                {Math.min(currentPage * itemsPerPage, (products.length + ((currentPage - 1) * itemsPerPage)))}
              </span> de{" "}
              <span className="font-medium">{totalPages * itemsPerPage}</span> resultados
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Próxima
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {selectedProduct && (
        <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Detalhes do Produto</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div>Código</div>
                  <div className="font-mono">{selectedProduct.code}</div>
                </div>
                <div>
                  <div>Nome</div>
                  <div>{selectedProduct.product_name}</div>
                </div>
                <div>
                  <div>Categoria</div>
                  <div>{selectedProduct.category_name}</div>
                </div>
                <div>
                  <div>Fornecedor</div>
                  <div>{selectedProduct.supplier_name}</div>
                </div>
                <div>
                  <div>Secretaria</div>
                  <div>{selectedProduct.department_sigla} - {selectedProduct.department_name}</div>
                </div>
                <div>
                  <div>Quantidade</div>
                  <div>{selectedProduct.quantity}</div>
                </div>
                <div>
                  <div>Status</div>
                  <div>
                    {getStatusBadge(selectedProduct.status)}
                  </div>
                </div>
                <div>
                  <div>Secretaria</div>
                  <div>{selectedProduct.department_name}</div>
                </div>
              </div>
              <div>
                <div>Código de Barras</div>
                <div className="flex justify-center p-4 bg-white">
                  <Barcode value={selectedProduct.code} />
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Products;